import { Coins } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Liten badge som visar current AI-credit-balance. Klicka → routa till
 * /billing. Döljs när user inte är inloggad.
 *
 * Färgad efter hälsa:
 *   grön  ≥ 100 credits
 *   gul   10-99 credits
 *   röd   < 10 credits
 */
export function CreditBalanceBadge({
  onClick,
  showUnit = false,
}: {
  onClick?: () => void;
  /** Append " ai" after the number. Default false (TopBar uses icon-only). */
  showUnit?: boolean;
}) {
  const { isAuthenticated } = useAuth();
  const { balanceFor, isLoading, ledgers } = useCreditBalance();

  if (!isAuthenticated || isLoading) return null;
  if (ledgers.length === 0) return null;

  const aiBalance = balanceFor('ai');
  const computeBalance = balanceFor('compute');
  const storageBalance = balanceFor('storage');

  let tone: 'ok' | 'warn' | 'critical' = 'ok';
  if (aiBalance < 10) tone = 'critical';
  else if (aiBalance < 100) tone = 'warn';

  const colorClass =
    tone === 'critical' ? 'bg-red-600 text-white hover:bg-red-700' :
    tone === 'warn' ? 'bg-amber-500 text-white hover:bg-amber-600' :
    'bg-emerald-600 text-white hover:bg-emerald-700';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className="rounded-md cursor-pointer"
          aria-label={`AI-credits: ${aiBalance}`}
        >
          <Badge className={`gap-1 ${colorClass}`}>
            <Coins className="w-3 h-3" />
            {aiBalance.toLocaleString()}
            {showUnit && ' ai'}
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs space-y-0.5">
          <div>AI credits: <strong>{aiBalance.toLocaleString()}</strong></div>
          <div>Compute: <strong>{computeBalance.toLocaleString()}</strong></div>
          <div>Storage: <strong>{storageBalance.toLocaleString()}</strong></div>
          {tone === 'critical' && (
            <div className="text-red-300 mt-1">Låg balans — överväg top-up</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
